import { useState } from 'react';
import { api } from '../api';
import { won } from '../format';
import { Alert, Dialog, errorText, Field } from './ui';

declare const __SELFON_DEMO__: boolean;
declare global { interface Window { PaypleCpayAuthCheck?: (obj: Record<string, unknown>) => void } }

/** 페이플이 요구하는 jQuery(문서 지정 버전). 무결성 해시로 바뀐 파일은 실행되지 않는다. */
const JQUERY = { src: 'https://ajax.googleapis.com/ajax/libs/jquery/3.4.1/jquery.min.js', integrity: 'sha384-vk5WoKIaW/vJyUAd9n/wmopsmNhiy+L2Z+SBxGYnUkunIxVxAv/UtMOhba/xskxh' };

/** 외부 결제 스크립트는 결제할 때만 한 번 불러온다(모든 화면에 넣지 않음). */
function loadScript(src: string, integrity?: string) {
  return new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const el = Object.assign(document.createElement('script'), { src, async: false });
    if (integrity) { el.integrity = integrity; el.crossOrigin = 'anonymous'; }
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('결제 모듈을 불러오지 못했습니다. 잠시 후 다시 시도하세요.'));
    document.head.append(el);
  });
}

/** 데모(단일 HTML)의 가짜 결제창. 카드 정보를 받지 않는다. */
function DemoPay({ order, onClose, onPay }: { order: { id: string; amount: number }; onClose: () => void; onPay: (cb: Record<string, unknown>) => void }) {
  return (
    <div className="card demo-pay" role="group" aria-label="데모 결제창">
      <strong>데모 결제창</strong>
      <p className="small muted">데모에서는 카드 정보 없이 결제 완료를 흉내 냅니다.</p>
      <div className="row-actions">
        <button type="button" className="btn" onClick={onClose}>닫기</button>
        <button type="button" className="btn btn-primary" onClick={() => onPay({
          PCD_PAY_RST: 'success', PCD_PAY_OID: order.id, PCD_PAY_TOTAL: String(order.amount), PCD_AUTH_KEY: 'demo', PCD_PAY_REQKEY: `demo:${order.id}:${order.amount}`,
        })}>{won(order.amount)} 결제 완료</button>
      </div>
    </div>
  );
}

/**
 * 알림톡 포인트 카드 충전. 금액은 서버가 정한 주문으로만 결제창을 열고, 결과는 서버가 다시 확인한다.
 * 데모(단일 HTML)는 실제 결제창 대신 화면 안의 가짜 결제창을 쓴다(카드 정보 입력 없음).
 */
export function CardTopup({ centerId, packs, mode, open, onClose, onDone }: {
  centerId: string; packs: number[]; mode: 'test' | 'live'; open: boolean; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [credits, setCredits] = useState(packs[1] ?? packs[0] ?? 10000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demoOrder, setDemoOrder] = useState<{ id: string; amount: number } | null>(null);

  const approve = async (oid: string, cb: Record<string, unknown>) => {
    try {
      const r = await api.approvePayment(centerId, oid, cb);
      if (r.order.status === 'PAID') onDone(`${r.order.credits.toLocaleString('ko-KR')}P 를 충전했습니다 (${won(r.order.amount)}).`);
      else onDone('결제 결과를 확인하고 있습니다. 결제 내역에서 상태를 확인하세요.');
      onClose();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); setDemoOrder(null); }
  };

  const pay = async () => {
    setBusy(true); setError(null);
    try {
      const { order, window: w } = await api.createPaymentOrder(centerId, { kind: 'MESSAGING_CREDIT', credits });
      if (__SELFON_DEMO__ || !w.scriptUrl) { setDemoOrder({ id: order.id, amount: order.amount }); return; }
      await loadScript(JQUERY.src, JQUERY.integrity);
      await loadScript(w.scriptUrl);
      if (!window.PaypleCpayAuthCheck) throw new Error('결제 모듈을 불러오지 못했습니다.');
      window.PaypleCpayAuthCheck({
        ...w.params,
        callbackFunction: (res: Record<string, unknown>) => {
          if (res.PCD_PAY_RST !== 'success') { setBusy(false); setError(res.PCD_PAY_RST === 'close' ? '결제창을 닫았습니다.' : String(res.PCD_PAY_MSG ?? '카드 인증에 실패했습니다.')); return; }
          void approve(order.id, res);
        },
      });
    } catch (e) { setError(errorText(e)); setBusy(false); }
  };

  return (
    <Dialog title="알림톡 포인트 충전" open={open} onClose={() => { setDemoOrder(null); setError(null); setBusy(false); onClose(); }}
      onSubmit={pay} keepOpen hideSubmit={Boolean(demoOrder) || busy} submitLabel={`${won(credits)} 카드 결제`}>
      {mode === 'test' && !__SELFON_DEMO__ && <Alert kind="info">테스트 결제입니다. 카드는 실제로 승인되고 24시간 안에 자동 취소됩니다.</Alert>}
      <fieldset className="radios" disabled={busy}>
        <legend>충전할 포인트 (1P = 1원, 부가세 포함)</legend>
        {packs.map((n) => (
          <label key={n}><input type="radio" name="credit-pack" checked={credits === n} onChange={() => setCredits(n)} /> {n.toLocaleString('ko-KR')}P · {won(n)}</label>
        ))}
      </fieldset>
      {demoOrder && <DemoPay order={demoOrder} onClose={() => { setDemoOrder(null); setBusy(false); setError('결제창을 닫았습니다.'); }} onPay={(cb) => void approve(demoOrder.id, cb)} />}
      {error && <Alert>{error}</Alert>}
    </Dialog>
  );
}

/**
 * 정기결제 카드 등록. 결제창에서 카드만 등록하고(결제 없음) 받은 키를 서버가 PG 에 다시 확인해 암호화 보관한다.
 * 자동 결제에 동의해야 등록할 수 있다.
 */
export function SubscriptionCard({ centerId, open, onClose, onDone }: { centerId: string; open: boolean; onClose: () => void; onDone: (msg: string) => void }) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demo, setDemo] = useState(false);
  const close = () => { setError(null); setBusy(false); setDemo(false); setConsent(false); onClose(); };

  const save = async (payerId: string) => {
    try {
      await api.saveSubscriptionCard(centerId, { PCD_PAYER_ID: payerId, email, consent: true });
      onDone('정기결제 카드를 등록했습니다.');
      close();
    } catch (e) { setError(errorText(e)); setBusy(false); setDemo(false); }
  };
  const open_ = async () => {
    if (!consent) { setError('자동 결제에 동의해 주세요.'); return; }
    setBusy(true); setError(null);
    try {
      const w = await api.subscriptionCardWindow(centerId);
      if (__SELFON_DEMO__ || !w.scriptUrl) { setDemo(true); return; }
      await loadScript(JQUERY.src, JQUERY.integrity);
      await loadScript(w.scriptUrl);
      if (!window.PaypleCpayAuthCheck) throw new Error('결제 모듈을 불러오지 못했습니다.');
      window.PaypleCpayAuthCheck({
        ...w.params,
        callbackFunction: (res: Record<string, unknown>) => {
          if (res.PCD_PAY_RST !== 'success' || typeof res.PCD_PAYER_ID !== 'string') { setBusy(false); setError(res.PCD_PAY_RST === 'close' ? '카드 등록 창을 닫았습니다.' : String(res.PCD_PAY_MSG ?? '카드를 등록하지 못했습니다.')); return; }
          void save(res.PCD_PAYER_ID);
        },
      });
    } catch (e) { setError(errorText(e)); setBusy(false); }
  };

  return (
    <Dialog title="정기결제 카드 등록" open={open} onClose={close} onSubmit={open_} keepOpen hideSubmit={demo || busy} submitLabel="카드 등록">
      <p className="muted small">등록할 때는 결제되지 않습니다. 본사가 요금제 신청을 승인하면 첫 결제를 하고, 이후 매월 같은 날 자동 결제합니다. 카드번호는 SelfOn에 저장되지 않습니다.</p>
      <Field id="sub-mail" label="결제 알림 메일" hint="결제 실패 안내를 받을 주소입니다.">
        <input id="sub-mail" type="email" required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} aria-describedby="sub-mail-hint" disabled={busy} />
      </Field>
      <label><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={busy} /> 신청한 요금제의 월 요금이 매월 자동 결제되는 데 동의합니다. 해지는 센터 설정에서 언제든 할 수 있습니다.</label>
      {demo && (
        <div className="card demo-pay" role="group" aria-label="데모 카드 등록">
          <p className="small muted">데모에서는 카드 정보 없이 등록을 흉내 냅니다.</p>
          <button type="button" className="btn btn-primary" onClick={() => void save('demo-card')}>카드 등록 완료</button>
        </div>
      )}
      {error && <Alert>{error}</Alert>}
    </Dialog>
  );
}

/** 학생 월 이용권: 보호자가 자기 계정에서 결제창으로 결제한다. 카드 정보는 PG 만 다룬다. */
export function StudentPassPay({ student, price, onClose, onDone }: {
  student: { studentId: string; studentName: string } | null; price: number; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demoOrder, setDemoOrder] = useState<{ id: string; amount: number } | null>(null);
  const close = () => { setDemoOrder(null); setError(null); setBusy(false); onClose(); };

  const approve = async (oid: string, cb: Record<string, unknown>) => {
    try {
      const r = await api.approvePass(student!.studentId, oid, cb);
      onDone(r.order.status === 'PAID' ? `${student!.studentName}님의 월 이용권을 결제했습니다 (${won(r.order.amount)}).` : '결제 결과를 확인하고 있습니다. 잠시 후 다시 확인해 주세요.');
      close();
    } catch (e) { setError(errorText(e)); setDemoOrder(null); setBusy(false); }
  };
  const pay = async () => {
    setBusy(true); setError(null);
    try {
      const { order, window: w } = await api.createPassOrder(student!.studentId);
      if (__SELFON_DEMO__ || !w.scriptUrl) { setDemoOrder({ id: order.id, amount: order.amount }); return; }
      await loadScript(JQUERY.src, JQUERY.integrity);
      await loadScript(w.scriptUrl);
      if (!window.PaypleCpayAuthCheck) throw new Error('결제 모듈을 불러오지 못했습니다.');
      window.PaypleCpayAuthCheck({
        ...w.params,
        callbackFunction: (res: Record<string, unknown>) => {
          if (res.PCD_PAY_RST !== 'success') { setBusy(false); setError(res.PCD_PAY_RST === 'close' ? '결제창을 닫았습니다.' : String(res.PCD_PAY_MSG ?? '카드 인증에 실패했습니다.')); return; }
          void approve(order.id, res);
        },
      });
    } catch (e) { setError(errorText(e)); setBusy(false); }
  };

  return (
    <Dialog title="학생 월 이용권 결제" open={Boolean(student)} onClose={close} onSubmit={pay} keepOpen hideSubmit={Boolean(demoOrder) || busy} submitLabel={`${won(price)} 카드 결제`}>
      <p>{student?.studentName}님의 이용 기간을 한 달 늘립니다. 자동 결제가 아니며, 결제할 때마다 보호자님이 직접 승인합니다.</p>
      <p className="muted small">임시 가격입니다. 카드 번호는 SelfOn에 저장되지 않습니다.</p>
      {demoOrder && <DemoPay order={demoOrder} onClose={() => { setDemoOrder(null); setBusy(false); setError('결제창을 닫았습니다.'); }} onPay={(cb) => void approve(demoOrder.id, cb)} />}
      {error && <Alert>{error}</Alert>}
    </Dialog>
  );
}
